import { Injectable } from '@angular/core';
import { asArray, getItemsDividedByCategories, isNull, toMap } from '../musimap-common/musimap-common.utils';
import { MusimapFiltersStorage } from './musimap-filters.storage';
import { combineLatest, noop, Observable } from 'rxjs';
import {
  MUSIMAP_DIVISIONS,
  MUSIMAP_FILTERS,
  MUSIMAP_OPERATORS,
  MUSIMAP_SELECTION_STATES,
  MUSIMAP_WIDGETS,
} from '../musimap-common/musimap-common.enum';
import { IMusimapFilterCore, IMusimapPreferencePrototype } from '../musimap-common/musimap-common.interface';
import { MusimapFiltersQuery } from './musimap-filters.query';
import { MusimapApiService } from './musimap-api.service';
import {
  getDefaultFiltersSettings,
  getExtendedDivisionsList,
  getExtendedDivisionsListWithMLR,
} from '../musimap-common/musimap-defaults.lib';
import { MusimapFilterModel } from '../musimap-models/musimap-filter.model';
import { MusimapCollectionProps } from '../musimap-models/musimap-collection.model';
import { SearchTermsQuery } from '../../state';
import { ESearchScopes } from '../../state/search-term.model';
import { map } from 'rxjs/operators';
import { EUserDivision } from '../../../user/user.model';
import { FeatureFlagApiService } from '../../../abstract/services/feature-flag-api.service';
import { FEATURE_FLAG } from '../../../abstract/shared/enum';
import { modifyChartYear } from '../../filters/state/utils';

const WORK_FILTERS: Set<string> = new Set<string>([
  MUSIMAP_FILTERS.COPYRIGHT_YEAR,
  MUSIMAP_FILTERS.PUBLISHING_CONTROL,
  MUSIMAP_FILTERS.THEME,
]);

const CHART_YEARS = ['1950', '1960', '1970', '1980', '1990', '2000', '2010', '2020'];

const RECORDING_FILTERS: Set<string> = new Set<string>(Object.values(MUSIMAP_FILTERS));

const QUERY_BUCKETS = {
  DIVISIONS: 'divisions',
  TAGS_FILTERS: 'tagsFilters',
  INCLUDE_FILTER: 'includeFilter',
  INCLUDE_FILTERS: 'includeFilters',
  EXCLUDE_FILTER: 'excludeFilter',
  EXCLUDE_FILTERS: 'excludeFilters',
  RANGE_SEARCH_FILTERS: 'rangeSearchFilter',
  MUSIMAP_SCORE_BASED_FIELD_SEARCH_FILTERS: 'musimapScoreBasedFieldSearchFilters',
  MUSIMAP_FIELD_SEARCH_FILTERS: 'musimapFieldSearchFilters',
  MUSIMAP_RANGE_SEARCH_FILTERS: 'musimapRangeSearchFilters',
  CHART_DATE_FILTERS: 'chartDateFilters',
  OPERATOR_BASED_FIELD_SEARCH_FILTERS: 'operatorBasedFieldSearchFilters',
  GENRE_FILTER: 'genreFilter',
  CLAUSES_FILTER: 'clausesFilter',
};

const OPERATOR_FIELD = 'operator';

const INCLUDED_PREFERENCE = 'included';

const EXCLUDED_PREFERENCE = 'excluded';

const getBucket = (filterName: string): string => {
  switch (filterName) {
    case MUSIMAP_FILTERS.DURATION:
    case MUSIMAP_FILTERS.BPM:
    case MUSIMAP_FILTERS.RELEASE_YEAR:
    case MUSIMAP_FILTERS.COPYRIGHT_YEAR:
    case MUSIMAP_FILTERS.PUBLISHING_CONTROL:
    case MUSIMAP_FILTERS.CHART_RANGE:
      return QUERY_BUCKETS.RANGE_SEARCH_FILTERS;

    case MUSIMAP_FILTERS.VERSION:
    case MUSIMAP_FILTERS.RECORD_LABEL:
    case MUSIMAP_FILTERS.ASSET_TYPE:
    case MUSIMAP_FILTERS.VOCAL_MIX:
    case MUSIMAP_FILTERS.ROC:
    case MUSIMAP_FILTERS.GCDM_VERSION:
    case MUSIMAP_FILTERS.RATING:
      return QUERY_BUCKETS.TAGS_FILTERS;

    case MUSIMAP_FILTERS.ISRC:
    case MUSIMAP_FILTERS.ALBUM:
    case MUSIMAP_FILTERS.ORIGINAL_ALBUM:
    case MUSIMAP_FILTERS.TITLE:
    case MUSIMAP_FILTERS.LABEL:
    case MUSIMAP_FILTERS.ARTIST:
    case MUSIMAP_FILTERS.WRITER:
    case MUSIMAP_FILTERS.LYRICS:
    case MUSIMAP_FILTERS.PROFIT_CENTER:
      return QUERY_BUCKETS.CLAUSES_FILTER;

    case MUSIMAP_FILTERS.DANCEABILITY:
    case MUSIMAP_FILTERS.SONIC_DENSITY:
      return QUERY_BUCKETS.MUSIMAP_RANGE_SEARCH_FILTERS;

    case MUSIMAP_FILTERS.CHART_DATES:
      return QUERY_BUCKETS.CHART_DATE_FILTERS;

    case MUSIMAP_FILTERS.MOOD:
    case MUSIMAP_FILTERS.VOCALS:
    case MUSIMAP_FILTERS.KEY:
    case MUSIMAP_FILTERS.LANGUAGE:
    case MUSIMAP_FILTERS.GENRES:
      return QUERY_BUCKETS.MUSIMAP_FIELD_SEARCH_FILTERS;

    case MUSIMAP_FILTERS.THEME:
      return QUERY_BUCKETS.OPERATOR_BASED_FIELD_SEARCH_FILTERS;
  }
};

@Injectable()
export class MusimapFiltersService {
  public hasSearchTerms$: Observable<boolean>;

  constructor(
    private searchTermsQuery: SearchTermsQuery,
    private filtersStorage: MusimapFiltersStorage,
    private filtersQuery: MusimapFiltersQuery,
    private musimapApi: MusimapApiService,
    private featureFlagApiService: FeatureFlagApiService
  ) {
    this.hasSearchTerms$ = this.searchTermsQuery.selectActiveTerms$.pipe(map(s => !!s.clauses?.length));
    this.init();
  }

  private init = (): this => {
    const { musimapApi, filtersStorage, filtersQuery } = this;
    const { filtersCollection } = filtersQuery;

    const getDefaultValue = (filter: IMusimapFilterCore, divisions: MUSIMAP_DIVISIONS[]): any => {
      switch (filter.type) {
        case MUSIMAP_WIDGETS.DIVISIONS_SELECTOR:
          return divisions;
        case MUSIMAP_WIDGETS.RANGE_SELECTOR:
        case MUSIMAP_WIDGETS.CHART_RANGE_SELECTOR:
          return [filter.min, filter.max];
        default:
          return filter.operator || null;
      }
    };

    combineLatest([
      musimapApi.loadMusimapFilters(),
      musimapApi.loadExtendedDivisions(),
      musimapApi.loadPreferences(),
      this.featureFlagApiService.getFlag(FEATURE_FLAG.ENABLE_AIMS),
    ]).subscribe(([filters, divisions, preferences, isAimsEnabled]) => {
      const filtersMap = new Map(filters.map(filter => [filter.label, filter]));
      const defaultFilersSettings = getDefaultFiltersSettings();
      if (!isAimsEnabled) {
        defaultFilersSettings[MUSIMAP_FILTERS.GCDM_VERSION].type = MUSIMAP_WIDGETS.TEXT_SELECTOR;
        defaultFilersSettings[MUSIMAP_FILTERS.PROFIT_CENTER].type = MUSIMAP_WIDGETS.TEXT_SELECTOR;
      }

      filtersCollection.add(
        Object.keys(defaultFilersSettings).map(key => {
          const originalFilter: IMusimapFilterCore = filtersMap.get(key) || { label: key, name: key };
          const filter: IMusimapFilterCore = Object.assign({}, originalFilter, defaultFilersSettings[key]);

          if (filter?.name === 'genres') {
            filter.name = 'sourced_genres';
          }

          if (filter.sources?.length) {
            filter.values = {};
            filter.sources = asArray(filter.sources).map(
              sourceName => filtersMap.get(sourceName as string) || { label: sourceName, name: sourceName }
            );
            filter.sources.forEach(
              source => (filter.values[source.label] = source.values || source.includeFilter?.values || [])
            );
          }

          const setDivisionsList = (): any[] => {
            const userDivision = this.musimapApi.userDivision;
            if (filter.label === MUSIMAP_FILTERS.DIVISIONS) {
              if ([EUserDivision.WMG_WCM_MLR, EUserDivision.MLR, EUserDivision.ALL].includes(userDivision)) {
                return getExtendedDivisionsListWithMLR();
              } else {
                return getExtendedDivisionsList();
              }
            }

            return [];
          };

          filter.sources = filter.sources || [originalFilter];
          filter.values = filter.values || filter.includeFilter?.values || setDivisionsList();

          return new MusimapFilterModel(filter, getDefaultValue(filter, divisions));
        })
      );

      this.fillInitialPreferences(preferences);

      filtersCollection.getItem(MUSIMAP_FILTERS.DIVISIONS)?.items.select(divisions);

      filtersStorage.update(state => ({
        ...state,
        isReady: true,
      }));
    });

    filtersQuery.isReady$.subscribe(isReady => {
      if (isReady) {
        this.handleOnQueryChangeEvent().handleOnScopeChangeEvent();
      }
    });

    return this;
  };

  private handleOnQueryChangeEvent = (): this => {
    const { filtersStorage, filtersQuery } = this;
    const { filtersCollection } = filtersQuery;

    combineLatest([
      this.hasSearchTerms$,
      combineLatest(filtersCollection.items.map(item => item.asObservable())),
    ]).subscribe(([hasSearchTerms]) => {
      const divisionsFilter: MusimapFilterModel = filtersCollection.getItem(MUSIMAP_FILTERS.DIVISIONS);
      const params = {
        [QUERY_BUCKETS.DIVISIONS]: divisionsFilter?.items?.selection?.map(d => d.label) || [],
      };

      let hasActiveFilters = divisionsFilter.isActive.value;
      let hasActivePreferences = false;

      const getQueryTemplate = (filter: MusimapFilterModel, queryTemplate: any): any => ({
        ...queryTemplate,
        [OPERATOR_FIELD]: filter.isConjunctive
          ? filter.isConjunctive.value
            ? MUSIMAP_OPERATORS.AND
            : MUSIMAP_OPERATORS.OR
          : undefined,
      });

      const getActiveItems = (filter: MusimapFilterModel): any => {
        const selection = filter.getItems();
        const preference = filter.getItems(true, true);
        const items = [];

        selection.forEach((itemsSet, key) => {
          const preferenceAsMap = toMap(preference.get(key) || []);
          itemsSet.forEach(item => {
            preferenceAsMap.delete(item.label);
            items.push(item);
          });

          preferenceAsMap.forEach(item => items.push(item));
          preference.delete(key);
        });

        if (preference.size) {
          preference.forEach(itemsSet => itemsSet.forEach(item => items.push(item)));
        }

        return items;
      };

      const fillMusimapTagsBucket = (selection, source, bucket) => {
        const { includedItems, excludedItems } = getItemsDividedByCategories(selection);
        const filterTemplate = { ...source[QUERY_BUCKETS.INCLUDE_FILTER] };
        const includeFilter = includedItems.length
          ? { ...filterTemplate, values: includedItems.map(item => item.label), label: source.label }
          : undefined;
        const excludeFilter = excludedItems.length
          ? { ...filterTemplate, values: excludedItems.map(item => item.label), label: source.label }
          : undefined;

        if (includeFilter || excludeFilter) {
          params[bucket] = params[bucket] || [];
          params[bucket].push({
            operator: source?.operator,
            label: source?.label,
            name: source?.name,
            includeFilter,
            excludeFilter,
          });
        }
      };

      const fillMusimapChartBucket = (selection, source, bucket) => {
        const { includedItems, excludedItems } = getItemsDividedByCategories(selection);
        const filterTemplate = { ...source[QUERY_BUCKETS.INCLUDE_FILTER] };
        const includeFilter =
          selection?.length && selection.length === CHART_YEARS.length
            ? includedItems.length
              ? {
                  ...filterTemplate,
                  values: [
                    {
                      fromYear: 1950,
                      toYear: new Date().getFullYear(),
                    },
                  ],
                }
              : undefined
            : includedItems.length
            ? {
                ...filterTemplate,
                values: includedItems.map(item => ({
                  fromYear: item.fromYear || +item.label,
                  toYear: item.toYear || modifyChartYear(+item.label),
                })),
              }
            : undefined;

        const excludeFilter = includedItems.length
          ? {
              ...filterTemplate,
              values: excludedItems.map(item => ({
                fromYear: item.fromYear,
                toYear: item.toYear,
              })),
            }
          : undefined;

        if (includeFilter || excludeFilter) {
          params[bucket] = params[bucket] || [];
          params[bucket].push({
            ...source,
            includeFilter,
            excludeFilter,
          });
        }
      };

      const fillClausesBucket = (selection, source) => {
        const { includedItems, excludedItems } = getItemsDividedByCategories(selection);
        if (includedItems.length) {
          const array = includedItems.map(item => item.label);
          const queries = Array.from(new Set(array));
          const type =
            source.name === MUSIMAP_FILTERS.ORIGINAL_ALBUM
              ? 'ORIGINAL_ALBUM'
              : source.name === 'profit_center.partial'
              ? 'PROFIT_CENTER'
              : source.name.toUpperCase();

          params[QUERY_BUCKETS.CLAUSES_FILTER] = params[QUERY_BUCKETS.CLAUSES_FILTER] || [];
          params[QUERY_BUCKETS.CLAUSES_FILTER].push({
            type,
            queries,
          });
        }

        if (excludedItems.length) {
          excludedItems.forEach(item => {
            const clausesItem = params[QUERY_BUCKETS.CLAUSES_FILTER]?.find(cf => cf.type === item.parent.toUpperCase());
            if (clausesItem) {
              clausesItem.queries = clausesItem.queries.filter(c => c !== item.label);
            }
          });
        }
      };

      const fillTagsBucket = (selection, source) => {
        const { includedItems, excludedItems } = getItemsDividedByCategories(selection);
        if (includedItems.length) {
          params[QUERY_BUCKETS.INCLUDE_FILTERS] = params[QUERY_BUCKETS.INCLUDE_FILTERS] || [];
          params[QUERY_BUCKETS.INCLUDE_FILTERS].push({
            name: source.name,
            values: includedItems.map(item => item.label),
          });
        }
        if (excludedItems.length) {
          params[QUERY_BUCKETS.EXCLUDE_FILTERS] = params[QUERY_BUCKETS.EXCLUDE_FILTERS] || [];
          params[QUERY_BUCKETS.EXCLUDE_FILTERS].push({
            name: source.name,
            values: excludedItems.map(item => item.label),
          });
        }
      };

      const fillRangesBucket = (range, items, bucket, source) => {
        const selectedRange = range ? range.value : [items.selection?.value || 0, 100];
        params[bucket] = params[bucket] || [];
        params[bucket].push({
          ...source,
          min: selectedRange[0],
          max: selectedRange[1],
        });
      };

      const fillGenresBucket = (selection, source, bucket) => {
        const { includedItems, excludedItems } = getItemsDividedByCategories(selection);
        const genresBucketFilters = {};

        [includedItems, excludedItems].forEach((itemsSet, i) => {
          if (itemsSet.length) {
            const targetGroup = i ? QUERY_BUCKETS.EXCLUDE_FILTER : QUERY_BUCKETS.INCLUDE_FILTER;

            source.name = 'sourced_genres';

            genresBucketFilters[targetGroup] = {};
            itemsSet.forEach(item => {
              genresBucketFilters[targetGroup][item.category] = genresBucketFilters[targetGroup][item.category] || [];
              genresBucketFilters[targetGroup][item.category].push(item.label);
            });
          }
        });

        if (includedItems.length) {
          source.includeFilter = {
            ...source.includeFilter,
            values: genresBucketFilters[QUERY_BUCKETS.INCLUDE_FILTER],
          };
        } else {
          delete source[QUERY_BUCKETS.INCLUDE_FILTER];
        }

        if (excludedItems.length) {
          source.excludeFilter = {
            ...(source.includeFilter || {}),
            values: genresBucketFilters[QUERY_BUCKETS.EXCLUDE_FILTER],
          };
        } else {
          delete source[QUERY_BUCKETS.EXCLUDE_FILTER];
        }

        params[bucket] = source;
      };

      filtersCollection.items.forEach(filter => {
        const { sources, items, range, label, preferences } = filter;
        const isActive = filter.isActive.value;

        if (label === MUSIMAP_FILTERS.DIVISIONS || !filter.isEnabled.value || (!isActive && !preferences?.size)) {
          return;
        }

        hasActiveFilters = !!(hasActiveFilters || isActive);
        hasActivePreferences = !!(hasActivePreferences || preferences?.size);

        if (sources?.length === 1) {
          const bucket = getBucket(label);
          const source = getQueryTemplate(filter, sources[0]);

          switch (bucket) {
            case QUERY_BUCKETS.RANGE_SEARCH_FILTERS:
            case QUERY_BUCKETS.MUSIMAP_RANGE_SEARCH_FILTERS:
              fillRangesBucket(range, items, bucket, source);
              break;

            case QUERY_BUCKETS.CHART_DATE_FILTERS:
              fillMusimapChartBucket(getActiveItems(filter), source, bucket);
              break;

            case QUERY_BUCKETS.TAGS_FILTERS:
              fillTagsBucket(getActiveItems(filter), source);
              break;

            case QUERY_BUCKETS.CLAUSES_FILTER:
              fillClausesBucket(getActiveItems(filter), source);
              break;

            case QUERY_BUCKETS.MUSIMAP_FIELD_SEARCH_FILTERS:
            case QUERY_BUCKETS.OPERATOR_BASED_FIELD_SEARCH_FILTERS:
              fillMusimapTagsBucket(getActiveItems(filter), source, bucket);
              break;

            case QUERY_BUCKETS.GENRE_FILTER:
              fillGenresBucket(getActiveItems(filter), source, bucket);
              break;
          }
        } else {
          const sourcesMap = toMap(sources);
          const buckets = {};

          getActiveItems(filter).forEach(item => {
            const bucket = getBucket(item.category);
            buckets[bucket] = buckets[bucket] || [];
            buckets[bucket].push(item);
          });

          Object.keys(buckets).forEach(bucket => {
            const selection = buckets[bucket];
            const source = getQueryTemplate(filter, { ...(sourcesMap.get(selection[0].category) as any) });

            switch (bucket) {
              case QUERY_BUCKETS.CHART_DATE_FILTERS:
                fillMusimapChartBucket(selection, source, bucket);
                break;
              case QUERY_BUCKETS.TAGS_FILTERS:
                fillTagsBucket(selection, source);
                break;
            }
          });
        }
      });

      if (hasActiveFilters || hasActivePreferences || hasSearchTerms) {
        filtersStorage.update(state => ({
          ...state,
          params,
        }));
      }
    });

    return this;
  };

  private handleOnScopeChangeEvent = (): this => {
    const { searchTermsQuery, filtersQuery } = this;

    const updateMLRDivisionsCollection = (scope: ESearchScopes) => {
      if (this.musimapApi.userDivision === EUserDivision.MLR) {
        if (scope === ESearchScopes.WORKS) {
          filtersQuery.filtersCollection
            .getItem(MUSIMAP_FILTERS.DIVISIONS)
            .items.deselectAll()
            .select(MUSIMAP_DIVISIONS.MLR);
        } else {
          filtersQuery.filtersCollection.getItem(MUSIMAP_FILTERS.DIVISIONS).items.selectAll();
        }
      }
    };

    searchTermsQuery.selectSearchScope$.subscribe(scope => {
      updateMLRDivisionsCollection(scope);
      const setOfEnabledFilters = scope === ESearchScopes.WORKS ? WORK_FILTERS : RECORDING_FILTERS;
      filtersQuery.filtersCollection
        .getItems()
        .forEach(item => item.isEnabled.next(setOfEnabledFilters.has(item.label)));
    });
    return this;
  };

  private fillInitialPreferences = (preferences: IMusimapPreferencePrototype[]): this => {
    const { filtersCollection } = this.filtersQuery;

    try {
      preferences.forEach(p => {
        const filter = filtersCollection.getItem(p.name);

        asArray(p.ignored || []).forEach(ignoredPreference => filter.ignoredPreferences.add(ignoredPreference));

        if (filter) {
          const { type, items } = filter;

          if (
            type === MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR ||
            type === MUSIMAP_WIDGETS.CHART_SELECTOR ||
            type === MUSIMAP_WIDGETS.TEXT_SELECTOR ||
            type === MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR
          ) {
            items.getItems(p[INCLUDED_PREFERENCE]).forEach(item => {
              item.preference = MUSIMAP_SELECTION_STATES.INCLUDED_ALL;
              filter.preferences.add(item.label);
            });
            items.getItems(p[EXCLUDED_PREFERENCE]).forEach(item => {
              item.preference = MUSIMAP_SELECTION_STATES.EXCLUDED_ALL;
              filter.preferences.add(item.label);
            });
          } else if (
            [MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR, MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR].some(
              t => t === type
            )
          ) {
            const categories = {};

            [INCLUDED_PREFERENCE, EXCLUDED_PREFERENCE].forEach(selectionType => {
              p[selectionType].forEach(d => {
                const [category, item] = d.split('~');
                categories[category] = categories[category] || {};
                categories[category][selectionType] = categories[category][selectionType] || [];
                categories[category][selectionType].push(item);
              });
            });

            Object.keys(categories).forEach(key => {
              const category = (items.getItem(key) || {})[MusimapCollectionProps.CHILD_COLLECTION];
              if (category) {
                category.getItems(categories[key][INCLUDED_PREFERENCE] || []).forEach(item => {
                  item.preference = MUSIMAP_SELECTION_STATES.INCLUDED_ALL;
                  filter.preferences.add(`${key}~${item.label}`);
                });
                category.getItems(categories[key][EXCLUDED_PREFERENCE] || []).forEach(item => {
                  item.preference = MUSIMAP_SELECTION_STATES.EXCLUDED_ALL;
                  filter.preferences.add(`${key}~${item.label}`);
                });
              }
            });
          }
        }
      });
    } catch (err) {
      this.musimapApi
        .clearPreferences()
        .toPromise()
        .finally(() => console.log(err));
    }

    return this;
  };

  public updateSavedFilters = (callback: any = noop): this => {
    const preferencesQuery: IMusimapPreferencePrototype[] = [];

    this.filtersQuery.filtersCollection.getItems().forEach(filter => {
      if (!filter.preferences) {
        return;
      }

      const preference: IMusimapPreferencePrototype = {
        name: filter.label,
        included: [],
        excluded: [],
        ignored: [],
      };

      filter.getItems(true).forEach(items => {
        items.forEach(item => {
          const id =
            filter.type === MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR ||
            MUSIMAP_WIDGETS.CHART_SELECTOR ||
            MUSIMAP_WIDGETS.TEXT_SELECTOR ||
            MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR
              ? `${item.label}`
              : `${item.category}~${item.label}`;

          if (item.preference === MUSIMAP_SELECTION_STATES.INCLUDED_ALL) {
            preference.included.push(id);
          } else if (item.preference === MUSIMAP_SELECTION_STATES.EXCLUDED_ALL) {
            preference.excluded.push(id);
          }
          if (filter.ignoredPreferences.has(id)) {
            preference.ignored.push(id);
          }
        });
      });

      if (preference.excluded.length || preference.included.length) {
        preferencesQuery.push(preference);
      }
    });

    this.musimapApi.savePreferences(preferencesQuery).toPromise().finally(callback);

    return this;
  };

  public toggleHeaderPanel = (nextState: boolean | null = null): MusimapFiltersService => {
    const { additionalPanelIsOpened$ } = this.filtersQuery;
    additionalPanelIsOpened$.next(isNull(nextState) ? !additionalPanelIsOpened$.value : !!nextState);
    return this;
  };

  public toggleAsidePanel = (nextState: boolean | null = null): MusimapFiltersService => {
    const { asidePanelIsOpened$ } = this.filtersQuery;
    asidePanelIsOpened$.next(isNull(nextState) ? !asidePanelIsOpened$.value : !!nextState);
    return this;
  };

  public togglePanel = (panelName: string, state: boolean | null = null): MusimapFiltersService => {
    const { filtersCollection } = this.filtersQuery;
    const panel = filtersCollection.getItem(panelName);

    if (panel) {
      if (isNull(state) ? !filtersCollection.isSelected(panel) : !!state) {
        filtersCollection.select(panel);
      } else {
        filtersCollection.deselect(panel);
      }
    }
    return this;
  };

  public togglePreferenceIgnorance = (
    filterName: string,
    preferenceIds: string | string[],
    state: boolean | null = null
  ): this => {
    const filter = this.filtersQuery.filtersCollection.getItem(filterName);
    if (filter?.ignoredPreferences) {
      asArray(preferenceIds).forEach(preferenceId => {
        const nextState = isNull(state) ? !filter.ignoredPreferences.has(preferenceId) : !!state;
        if (nextState) {
          filter.ignoredPreferences.add(preferenceId);
        } else {
          filter.ignoredPreferences.delete(preferenceId);
        }
      });

      filter.update();
      return this.updateSavedFilters();
    } else {
      return this;
    }
  };

  public closeAllPanels = (): MusimapFiltersService => {
    this.filtersQuery.filtersCollection.deselectAll();
    return this;
  };

  public clearAllUnsaved = (): any => this.filtersQuery.filtersCollection.getItems().forEach(filter => filter.reset());

  public saveFilter = (filterName: string): any => {
    const filter: MusimapFilterModel = this.filtersQuery.filtersCollection.getItem(filterName);

    if (filter) {
      filter.savePreferences().update();
      return this.updateSavedFilters();
    } else {
      return this;
    }
  };

  public clearSavedFilter = (filterName: string, categoryName: string | null = null): MusimapFiltersService => {
    const filter: MusimapFilterModel = this.filtersQuery.filtersCollection.getItem(filterName);
    filter?.clearPreferences(categoryName);
    return this.updateSavedFilters();
  };
}
